import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { updateBreadcrumbs } from '~src/app/actions/UpdateBreadcrumbsAction';
import { getAppStore } from '~src/app/AppStore';
import { Messages } from '~src/codegen/Localize';
import { RequestAccessError } from '~src/requestAccess/components/RequestAccessError';
import { INamedItemInfo } from '~src/ts/models/resourceInfoParts';

import { setOrderAction } from '../app/actions/SetOrderAction';
import { contentCountsUpToDate } from '../app/actions/spaces/ContentCountsActions';
import { enterSpace } from '../app/actions/spaces/EnterSpaceAction';
import { requestSpaceContents } from '../app/actions/spaces/RequestSpaceContentsActions';
import { setPreviousUrl } from '../app/actions/spaces/SetPreviousUrlReducerAction';
import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs
} from '../app/content/breadcrumbs/BreadcrumbHelpers';
import {
  ConnectedQueryParamUpdateProvider,
  QueryParamUpdateProviderOwnProps
} from '../app/spaces/RouterHelpers/QueryParamUpdateProvider';
import { ConnectedSpaceInfoProvider } from '../app/spaces/RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../app/spaces/SpacesSiteUrls';
import { LensSubspaceName, SpaceInfo } from '../app/state/SpaceInfo';
import { MapDispatchToProps } from '../app/utils/ConnectUtils';
import { BrowserTitleService } from '../commonInfra/services/BrowserTitleService';
import { sanitize } from '../commonInfra/utils/Sanitize';
import { Projects } from '../projects/resources/Projects';
import { Filter } from '../ts/helpers/Filter';
import { Omit } from '../ts/helpers/TypescriptHelpers';
import { ILensDetailInfo, ILensInfo } from '../ts/models/ILensInfo';
import { Lenses } from '../ts/resources/Lenses';
import { ILensActions } from '../ts/ServerApi/types/ILensAction';
import { LensesTAGHandler } from './api/LensTAG';
import { ConnectedLensSpace } from './components/ConnectedLensSpace';
import { LensSpaceOwnProps, LensSpaceProps } from './components/LensSpace';

export interface LensSpaceProviderOwnProps {
  readonly routeLocationSearch: string;
  readonly siteName: string;
  readonly subspaceName: LensSubspaceName;
  readonly lensId: string;
  readonly urls: SpacesSiteUrls;
  readonly renderLensSpace: (props: LensSpaceOwnProps) => JSX.Element;
  readonly fetchedAllData: () => void;
}

export interface LensSpaceProviderDispatchProps {
  readonly contentCountsUpToDate: () => void;
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly setPreviousUrl: (previousUrl: string) => void;
}

export type LensSpaceProviderContainerProps = LensSpaceProviderOwnProps & LensSpaceProviderDispatchProps;

export interface LensSpaceProviderContainerState {
  readonly lensActions: ILensActions;
  readonly lensInfo: ILensInfo;
  readonly lensDetailInfo: ILensDetailInfo;
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly canEdit: boolean;
  readonly errorCode: number;
}

const InitialState: LensSpaceProviderContainerState = {
  lensActions: undefined,
  lensDetailInfo: undefined,
  lensInfo: undefined,
  initialRender: true,
  renderChildren: false,
  hasError: false,
  canEdit: false,
  errorCode: undefined,
};

export class LensSpaceProviderContainer extends React.PureComponent<LensSpaceProviderContainerProps, LensSpaceProviderContainerState> {

  private projectHierarchy: INamedItemInfo[] | undefined = undefined;
  private unmounted: boolean = false;
  public state = InitialState;

  public render() {
    if (this.state.renderChildren) {
      return this.props.renderLensSpace({
        allowedActions: this.state.lensActions,
        lensDetailInfoWithUsage: {
          usageInfo: this.state.lensInfo.usageInfo,
          usageInfoMaxValues: this.state.lensInfo.usageInfoMaxValues,
          ...this.state.lensDetailInfo,
        },
        subspaceProps: {
          siteUrls: this.props.urls,
          lensDetailInfo: this.state.lensDetailInfo,
        },
        canEdit: this.state.canEdit,
        sanitizer: sanitize,
        reloadSpace: this.reloadSpace,
      });
    } else if (this.state.hasError) {
      return <RequestAccessError url={this.props.urls.exploreUrl()} contentIdentifier={this.props.lensId} contentType={'lens'} errorCode={this.state.errorCode} />;
    } else {
      return null;
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentDidUpdate(prevProps: LensSpaceProviderContainerProps) {
    if (this.changedLens(prevProps)) {
      this.updateState(InitialState);
      this.getData();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.changeSubspace(false, prevProps.subspaceName);

      this.setState({initialRender: false, renderChildren: true});
      BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: this.state.lensDetailInfo.name });

    } else if (prevProps.subspaceName !== this.props.subspaceName) {
      this.changeSubspace(true, prevProps.subspaceName);
    }
  }

  private getData = () => {
    this.getLens();
    this.getLensDetail();
    this.getActions();
    this.canEdit();
  }

  private changedLens(prevProps: LensSpaceProviderContainerProps) {
    return !_.isUndefined(this.props.siteName) &&
      !_.isUndefined(this.props.lensId) &&
      (prevProps.siteName !== this.props.siteName || prevProps.lensId !== this.props.lensId);
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.lensInfo) &&
      !_.isUndefined(this.state.lensDetailInfo) &&
      !_.isUndefined(this.state.lensActions);
  }

  private getLensDetail = async () => {
    try {
      const lensDetailInfo = await Lenses.getById(this.props.lensId);
      this.updateState({ lensDetailInfo });
    } catch (e) {
      this.updateState({hasError: true, errorCode: e as number});
    }
  }

  private getLens = async () => {
    try {
      const lensInfo = await Lenses.all()
        .withFilters([Filter.fieldEq('id', this.props.lensId)])
        .getFirst();
      this.updateState({lensInfo});
      if(this.props.subspaceName === 'usageAnalytics'){
        this.populateBreadcrumbsForUsageAnalytics();
      } else {
        await this.getProjectHierarchyAndUpdateBreadcrumbs(lensInfo);
      }
      this.props.fetchedAllData();
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getActions = async () => {
    try {
      const actions = await Lenses.actions(this.props.lensId);
      this.updateState({lensActions: actions});
    } catch (e) {}
  }

  private canEdit = async () => {
    try {
      const canEdit = await LensesTAGHandler.canEditLens(this.props.lensId);
      this.updateState({canEdit: canEdit});
    } catch (e) {}
  }

  private reloadSpace = this.getData;

  private updateState<K extends keyof LensSpaceProviderContainerState>(newState: Pick<LensSpaceProviderContainerState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }

  private changeSubspace = (shouldNotFetchCounts: boolean, prevSubspace: LensSubspaceName) => {
    this.props.enterSpace(
      {
        item: this.state.lensDetailInfo,
        spaceType: 'lens',
        subspaceName: this.props.subspaceName
      }
    );

    // If we just switched tabs we know content counts are up to date.
    // They will get set to false by the above EnterSpaceAction.
    if(shouldNotFetchCounts) {
      this.props.contentCountsUpToDate();
    }

    if (this.props.subspaceName === 'usageAnalytics') {
      this.populateBreadcrumbsForUsageAnalytics();

    } else if (prevSubspace === 'usageAnalytics') {
      if (_.isUndefined(this.projectHierarchy)) {
        // if the previous subspace was usageAnalytics, then we need to reset the breadcrumbs and fetch project hierarchy.
        // Otherwise, the breadcrumbs are already correct
        // don't need to await on this function because it does not affect requestSpaceContents
        this.getProjectHierarchyAndUpdateBreadcrumbs(this.state.lensInfo);
      } else {
        this.populateBreadcrumbsWithProjectHierarchy(this.state.lensInfo);
      }
    }

    this.props.requestSpaceContents();
  }

  private getProjectHierarchyAndUpdateBreadcrumbs = async (lensInfo: ILensInfo) => {
    this.projectHierarchy = await Projects.getProjectHierarchy(lensInfo.project?.id);
    this.populateBreadcrumbsWithProjectHierarchy(lensInfo);
  }

  private populateBreadcrumbsWithProjectHierarchy = (lensInfo: ILensInfo) => {
    const projectHierarchyWithType = addContentTypeToProjectHierarchy(this.projectHierarchy);
    populateContentBreadcrumbs([...projectHierarchyWithType, lensInfo], this.props.urls);
  }

  private populateBreadcrumbsForUsageAnalytics = () => {
    getAppStore().dispatch(updateBreadcrumbs([
      {
        text: this.state.lensDetailInfo.datasource.name,
        href: this.props.urls.datasourceUrl(this.state.lensDetailInfo.datasource.id),
      },
      {
        text: this.state.lensInfo.name,
        href: this.props.urls.lensUrl(this.state.lensInfo.id),
      },
      {
        text: Messages.datasourceAskDataMetrics(),
      }
    ]));
  }

}

const mapDispatchToProps: MapDispatchToProps<LensSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  contentCountsUpToDate,
  requestSpaceContents,
  enterSpace,
  setOrderAction,
  setPreviousUrl,
}, dispatch);

export const ConnectedLensSpaceProviderContainer: React.ComponentClass<LensSpaceProviderOwnProps>
  = connect(undefined, mapDispatchToProps)(LensSpaceProviderContainer);

const renderLensSpace = (props: LensSpaceProps): JSX.Element => <ConnectedLensSpace {...props} />;

export interface LocationPathProp {
  readonly routeLocationPath: string;
}
export type LensSpaceProviderProps = LocationPathProp & Omit<LensSpaceProviderOwnProps, 'renderLensSpace' | 'fetchedAllData'>;

export interface LensSpaceProviderState {
  readonly fetchedAllData: boolean;
}

export class LensSpaceProvider extends React.PureComponent<LensSpaceProviderProps, LensSpaceProviderState> {
  public state: LensSpaceProviderState = {fetchedAllData: false};

  public render() {
    const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
      routeLocationPath: this.props.routeLocationPath,
      routeLocationSearch: this.props.routeLocationSearch,
      shouldUpdateContentTypes: false,
      shouldUpdateFilters: false,
      shouldUpdateSort: false,
      shouldUpdateBreadcrumbs: this.state.fetchedAllData,
    };

    const LensSpaceInfo: SpaceInfo = {
      item: null,
      spaceType: 'lens',
      subspaceName: this.props.subspaceName,
    };

    return (
      <ConnectedSpaceInfoProvider spaceInfo={LensSpaceInfo}>
        <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
          <ConnectedLensSpaceProviderContainer
            {...this.props}
            renderLensSpace={renderLensSpace}
            fetchedAllData={this.fetchedAllData} />
        </ConnectedQueryParamUpdateProvider>
      </ConnectedSpaceInfoProvider>
    );
  }

  private fetchedAllData = () => {
    this.setState({fetchedAllData: true});
  }
}
